import { Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { updateTuiTargetElementData } from '@office-start/teaching-ui-service';
import { logError, logFeature, logImpression, logUserAction } from '@office-start/telemetry-service';
import { KnownFeatureNames } from '@office-start/telemetry-types';
import { Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';

import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { ErrorId } from '../error-handler/error-id';
import { OfficeAuxiliaryRoute, OfficeNamedOutlet } from '../shared/constants/office-route.enum';
import { DropdownMenu } from '../shared/directives/dropdown-menu/dropdown-menu.component';
import { MenuSelectItemEvent } from '../shared/directives/menu/menu-select-item.model';
import { InstrumentationData } from '../shared/instrumentation/instrumentation-data.model';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { ObservableScope } from '../shared/models/observable-scope.enum';
import { ConsumerSkuType } from '../shared/services/consumer-license.service';
import { DeviceDetector, DeviceInfo, PlatformType } from '../shared/services/device-detector.service';
import { DocumentService } from '../shared/services/document.service';
import { EventService } from '../shared/services/event.service';
import { OfficeRouteService } from '../shared/services/office-route.service';
import { PerformanceService } from '../shared/services/performance.service';
import { LaunchAction, UrlLauncherService } from '../shared/services/url-launcher.service';
import { AngularEvents } from '../shared/shared-context/angular-events';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ConsumerInstallConfig, ConsumerInstallConfigService } from '../shared/shared-context/consumer-install-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { LocService } from '../shared/shared-context/loc.service';
import { WorkloadPublicDataConfigService } from '../shared/shared-context/workload-publicdata-config.service';
import { DimeExperience, UpsellDataService } from '../upsell/upsell-data.service';
import { InstallConfig, InstallConfigService } from './install-config.service';
import { ButtonScenario, InstallDataService, InstallInfo } from './install-data.service';
import { InstallLink } from './install-link.model';

import './install.component.css';

@Component({
    selector: 'ohp-install',
    templateUrl: './install.component.html'
})
export class InstallComponent implements OnInit, OnDestroy {
    public showInstallSymbol = false;
    public text: string;
    public ariaLabel: string;
    public id: string;
    public showCommercialButton: boolean;
    public clickData: InstrumentationData;
    public showWizard: boolean;
    public installOfficeAppsButtonText: string;
    public installOfficeDescription: string;
    public isMobile = false;
    public showConsumerUpsell = false;
    public temporaryHideInstall = true;
    public expandConsumerInstallDropdown = false;
    public buyMicrosoft365BtnText: string;
    public goPremiumBtnText: string;
    public goPremiumDescription: string;
    public haveOfficeProductKeyLnkText: string;
    public needHelpInstallingOfficeLnkText: string;
    public buyOffice365Url: string;
    public buyOffice365DimeExperience: DimeExperience;
    public goPremiumUrl: string;
    public goPremiumDimeExperience: DimeExperience;
    public haveOfficeProductKeyUrl: string;
    public needHelpInstallingOfficeUrl: string;
    public isConsumer: boolean;
    public consumerInstallButtonRenderAllowed = true;

    // An additional link for commercial user
    public additionalLink: string;
    public additionalLinkAriaLabel: string;
    public additionalLinkText: string;
    public additionalLinkDescription: string;
    public additionalLinkId = 'install-additional-link';
    public menuModel: DropdownMenu;
    public installLinks: Array<InstallLink> = new Array();
    public fluentRefresh: boolean;

    private navigateToUrl: string;
    private authType: AuthType;
    private deviceInfo: DeviceInfo;
    private installConfig: InstallConfig;

    private outsideClickListener: Function;
    private outsideTouchListener: Function;

    private rightAligned: boolean = true;
    private resizeSubscription: Subscription;

    private readonly softwarePanelQueryParam = '&SoftwareManageLayout=';

    @ViewChild('installerBinaryDownload') public downloadIframeRef: ElementRef;
    @ViewChild('installContainer') public installContainer: ElementRef;
    @ViewChild('consumerInstallContainer') public consumerInstallContainer: ElementRef;

    @Input() public appFilter: string;

    @Input() public componentArea: string;

    @Input() public showInstallButton: boolean;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly consumerInstallConfigService: ConsumerInstallConfigService,
        private readonly contextService: ContextService,
        private readonly deviceDetector: DeviceDetector,
        private readonly documentService: DocumentService,
        private readonly eventService: EventService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly installConfigService: InstallConfigService,
        private readonly installDataService: InstallDataService,
        private readonly instrumentationService: InstrumentationService,
        private readonly locService: LocService,
        private readonly officeRouteService: OfficeRouteService,
        private readonly performanceService: PerformanceService,
        private readonly renderer: Renderer2,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly upsellDataService: UpsellDataService,
        private readonly urlLauncherService: UrlLauncherService,
        private readonly consumerPublicDataService: WorkloadPublicDataConfigService
    ) {
    }

    public ngOnInit() {
        if (!this.appFilter) {
            throw new Error('No appFilter provided.');
        }

        if (this.appFilter !== 'Office' && this.appFilter !== 'Project' && this.appFilter !== 'Visio') {
            return;
        }

        this.authType = this.startupConfigService.config.authType;
        this.isConsumer = this.authType === AuthType.MSA;
        this.showCommercialButton = this.authType === AuthType.OrgId;
        this.deviceInfo = this.deviceDetector.getDeviceInfo();
        this.installConfig = this.installConfigService.config;
        this.temporaryHideInstall = false;
        this.fluentRefresh = this.capabilitiesService.capabilities.fluentRefresh;

        if (this.isConsumer) {
            this.temporaryHideInstall = true;
            const initializeConsumerInstallInfo = (config: ConsumerInstallConfig, hasBusinessLicense: boolean = false) => {
                const subscriptionStatus = config && config.currentSkuInfo ? config.currentSkuInfo.skuType : ConsumerSkuType.NotDetected;
                const isPipl = this.startupConfigService.config.isPipl;
                const userUpsellable = (subscriptionStatus === ConsumerSkuType.NotSubscribed || subscriptionStatus === ConsumerSkuType.NonActive) && !hasBusinessLicense;
                this.consumerInstallButtonRenderAllowed = !(userUpsellable && isPipl); // hide install button for free users with Pipl policy enabled
                this.showConsumerUpsell = userUpsellable && !isPipl; // show upsell to all upsellsable users where no Pipl policy is applicable
                this.temporaryHideInstall = false;

                if (this.showConsumerUpsell) {
                    if (this.installConfigService.config.dimeEnabled) {
                        this.upsellDataService.warmupDime(DimeExperience.BuyButton);
                        this.upsellDataService.warmupDime(DimeExperience.InstallGoPremiumButton);
                        this.eventService.whenEventFired(AngularEvents.convertedToPaid, ObservableScope.OnlyFuture)
                            .then(() => { this.showConsumerUpsell = false; });
                    }

                    const resources = this.installConfig.resources;
                    this.goPremiumBtnText = resources && resources.GoPremiumBtnText;
                    this.goPremiumDescription = resources && this.capabilitiesService.capabilities.altaBrandingChangesEnabled ? resources.GoPremiumDescription.replace(/<c0>/g, '<span class="alta-highlighted-text">').replace(/<\/c0>/g, '</span>') : resources.GoPremiumDescription.replace(/<c0>/g, '<span class="highlighted-text">').replace(/<\/c0>/g, '</span>');
                    this.haveOfficeProductKeyLnkText = resources && resources.HaveOfficeProductKeyLnkText;
                    this.needHelpInstallingOfficeLnkText = resources && resources.NeedHelpInstallingOfficeLnkText;
                    this.haveOfficeProductKeyUrl = this.installConfig.haveOfficeProductKeyUrl;
                    this.needHelpInstallingOfficeUrl = this.installConfig.needHelpInstallingOfficeUrl;

                    this.buyMicrosoft365BtnText = resources?.BuyMicrosoft365BtnText;

                    const upsellTrackingParam = this.upsellDataService.getUpsellTrackingParam();
                    this.buyOffice365Url = this.installConfig.buyOffice365Url.replace('{OEM}', upsellTrackingParam);
                    this.buyOffice365DimeExperience = DimeExperience.BuyButton;
                    this.goPremiumUrl = this.installConfig.goPremiumUrl.replace('{OEM}', upsellTrackingParam);
                    this.goPremiumDimeExperience = DimeExperience.InstallGoPremiumButton;

                    // Handle click after consumer install dropdown expanded
                    const outsideClickHandler = (event: MouseEvent | TouchEvent) => {
                        if (this.expandConsumerInstallDropdown && this.consumerInstallContainer && !this.consumerInstallContainer.nativeElement.contains(event.target)) {
                            this.onToggleConsumerDropdown(true);
                        }
                    };

                    this.outsideClickListener = this.renderer.listen(this.documentService.nativeDocument, 'click', outsideClickHandler);
                    this.outsideTouchListener = this.renderer.listen(this.documentService.nativeDocument, 'touchend', outsideClickHandler);

                    // Log consumer upsell install impression
                    const params: InstrumentationParams = {};
                    params[InstrumentationParam.SkuType] = ConsumerSkuType[subscriptionStatus];
                    this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.ConsumerUpsellImpression, params);
                    logImpression({
                        eventName: KnownInstrumentationEvents.ConsumerUpsellImpression,
                        area: 'Hero',
                        subArea: 'Install',
                        distribution: ConsumerSkuType[subscriptionStatus]
                    });
                }
            };

            if (this.capabilitiesService.capabilities.workloadPublicDataEnabled) {
                Promise.all([this.consumerPublicDataService.getConfig(), this.consumerInstallConfigService.getConfig()]).then(([consumerPublicDataConfig, consumerInstallConfig]) => {
                    initializeConsumerInstallInfo(consumerInstallConfig, consumerPublicDataConfig.hasBusinessLicense);
                }).catch(initializeConsumerInstallInfo);
            } else {
                this.consumerInstallConfigService.getConfig().then(initializeConsumerInstallInfo);
            }
        }

        const notifyInstallButtonResponse = (info: InstallInfo) => {
            // Set timeout to ensure that button has rendered
            setTimeout(() => this.eventService.broadcastEvent({
                name: AngularEvents.installButtonResponse,
                args: [info ? info.text : '']
            }), 0);
        };

        // Fetch installer data
        this.installDataService.getGenericInstaller(this.contextService.sharedContext.officeMarket, this.deviceInfo, this.authType, this.appFilter)
            .then(info => {
                if (!info || info.scenario === ButtonScenario.Hide) {
                    notifyInstallButtonResponse(info);
                    this.logScenario(ButtonScenario.Hide);
                    return;
                }

                notifyInstallButtonResponse(info);
                this.renderButton(info);
                setTimeout(() => this.positionMenu(), 0);

                this.resizeSubscription = this.eventService.events.withScope(ObservableScope.OnlyFuture).pipe(
                    filter(event => event.name === AngularEvents.contentWidthChanged || event.name === AngularEvents.windowResized),
                    debounceTime(10)
                ).subscribe(_ => {
                    this.positionMenu();
                });

                if (!info.cached) {
                    const markerName = 'Install_Button_Ready_' + this.appFilter;
                    this.performanceService.mark(markerName);
                }

                if (info.scenario === ButtonScenario.DirectInstall && info.appId && this.authType === AuthType.OrgId) {
                    this.installDataService.getCustomInstaller(info.appId, this.contextService.sharedContext.officeMarket)
                        .then(customInstaller => {
                            if (customInstaller) {
                                this.navigateToUrl = customInstaller;
                                this.installLinks[0].url = customInstaller;
                            }
                        })
                        .catch(error => {
                            const params: InstrumentationParams = {};
                            if (typeof error === 'string') {
                                params[InstrumentationParam.Error] = error;
                            }
                            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.InstallCustomError, params);
                            logError({
                                eventName: KnownInstrumentationEvents.InstallCustomError,
                                errorType: 'Install_Error',
                                message: `Failed to getCustomInstaller for app ${this.appFilter}, ${info.appId} `,
                                debugInfo: typeof error === 'string' ? error : ''
                            });
                            this.globalErrorHandler.handleError(error, ErrorId.DPcFB);
                        });
                }
            })
            .catch(error => {
                // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                this.globalErrorHandler.handleError(error, ErrorId.mnAIg);

                notifyInstallButtonResponse(null);
                this.logScenario(ButtonScenario.Hide);

                const params: InstrumentationParams = {};
                if (typeof error === 'string') {
                    params[InstrumentationParam.Error] = error;
                }
                this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.InstallGenericError, params);
                logError({
                    eventName: KnownInstrumentationEvents.InstallGenericError,
                    errorType: 'Install_Error',
                    message: `Failed to getCustomInstaller for app ${this.appFilter} `,
                    debugInfo: typeof error === 'string' ? error : ''
                });
            });
    }

    public ngOnDestroy() {
        if (this.outsideClickListener) {
            this.outsideClickListener();
        }

        if (this.outsideTouchListener) {
            this.outsideTouchListener();
        }

        if (this.resizeSubscription) {
            this.resizeSubscription.unsubscribe();
        }

        updateTuiTargetElementData('Install_AppsButton', null);
    }

    /**
     * Takes action when a user clicks on the button to install Office,
     * try Office or go to the mobile Office product pages.
     */
    public action(event?: Event): void {
        event?.preventDefault();
        if (this.showWizard) {
            // IMPORTANT: Download installer executable via iframe instead of calling
            // openUrl with '_self' as target.
            // Some browsers will trigger onbeforeunload when '_self' is used to initiate
            // binary download. This event is used to track lifetime of page session
            // so it is critical that it is called only when user navigates away from the page
            // which is not the case with binary download.
            this.downloadIframeRef.nativeElement.setAttribute('src', this.navigateToUrl);

            this.officeRouteService.navigateToAuxilaryRoute(OfficeNamedOutlet.InstallWizard, OfficeAuxiliaryRoute.InstallWizardOpen);
        } else {
            // It is okay to call openUrl with '_blank' as target
            // because this will open new tab and page session lifecycle will not be impacted
            // PWA TODO: Is there a better way to invoke install from UWP?
            if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                import('@office-start/launcher-service').then(
                    launchSvc => {
                        launchSvc.launchUrl(this.navigateToUrl, {action: 'Install'});
                    }
                );
            } else {
                this.urlLauncherService.openUrl(this.navigateToUrl, LaunchAction.Install);
            }
        }
    }

    public onSelectItem(event: MenuSelectItemEvent): void {
        const item: InstallLink = event.item as InstallLink;
        if (item.getId() === this.additionalLinkId) {
            this.logClickData(this.clickData);
            if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                import('@office-start/launcher-service').then(
                    launchSvc => {
                        launchSvc.launchUrl(item.url, {action: 'InstallAdditional'});
                    }
                );
            } else {
                this.urlLauncherService.openUrl(item.url, LaunchAction.InstallAdditional);
            }
        } else {
            this.logClickData(this.clickData);
            this.action();
        }
    }

    public onToggleMenu(open: boolean): void {
        // log "expand" when click Install button to show dropdown
        if (open) {
            const targetId = 'Install-dropdown-expand';
            this.instrumentationService.logClickEvent(KnownInstrumentationEvents.InstallClick, targetId, this.componentArea);
            logUserAction({
                type: 'Click',
                id: targetId,
                area: 'Hero',
                subArea: 'Install',
                result: 'Expand',
                target: 'InstallDropdown',
                eventName: KnownInstrumentationEvents.InstallClick
            });
        }
    }

    public onToggleConsumerDropdown(forceClose: boolean = false): void {
        if (!this.showConsumerUpsell || this.isMobile) {
            this.logClickData(this.clickData);
            this.action();
            return;
        }

        // log "expand" when click consumer Install button to show dropdown
        if (!this.expandConsumerInstallDropdown) {
            const targetId = 'Consumer-Install-dropdown-expand';
            this.instrumentationService.logClickEvent(KnownInstrumentationEvents.InstallClick, targetId, this.componentArea);
            logUserAction({
                type: 'Click',
                id: targetId,
                area: 'Hero',
                subArea: 'Install',
                result: 'Expand',
                target: 'InstallDropdown',
                eventName: KnownInstrumentationEvents.InstallClick
            });
        }

        if (forceClose) {
            this.expandConsumerInstallDropdown = false;
        } else {
            this.expandConsumerInstallDropdown = !this.expandConsumerInstallDropdown;
        }
    }

    public launchDime(event: Event, experience: DimeExperience, targetUrl: string): void {
        if (experience === DimeExperience.InstallGoPremiumButton) {
            this.onToggleConsumerDropdown();
        }

        if (this.installConfigService.config.dimeEnabled) {
            event.preventDefault();
            this.upsellDataService.launchDime(experience, targetUrl);
        }
    }

    public positionMenu(): void {
        if (!this.installContainer?.nativeElement) {
            return;
        }

        const isRtl = this.contextService.isRtl;
        const position = this.installContainer.nativeElement.offsetLeft;
        const rightAligned = (!isRtl && position !== 0) || (isRtl && position === 0);

        if (rightAligned !== this.rightAligned) {
            this.rightAligned = rightAligned;
            this.menuModel.rightAligned = this.rightAligned;
        }

        updateTuiTargetElementData('Install_AppsButton', { element: this.installContainer.nativeElement });
    }

    private renderButton(info: InstallInfo): void {
        if (info.scenario === ButtonScenario.Hide) {
            return;
        }

        this.showInstallSymbol = info.scenario === ButtonScenario.DirectInstall || info.scenario === ButtonScenario.RedirectToMyAccountPage;
        this.ariaLabel = info.ariaLabel;
        this.id = `installbutton-${AuthType[this.authType]}-${info.scenario}`;
        this.navigateToUrl = info.url;
        this.isMobile = info.scenario === ButtonScenario.GetMobileApps;
        const resources = this.installConfigService.config.resources;

        if (this.appFilter !== 'Office') {
            this.text = !this.isMobile && resources ? this.locService.format(resources.DesktopApp, this.appFilter) : '';
            this.installOfficeAppsButtonText = resources && resources.InstallText;
            this.installOfficeDescription = resources ? this.locService.format(resources.InstallIndividualAppText, this.appFilter) : '';
        } else {
            this.text = this.capabilitiesService.capabilities.isPwaInUwp && this.installConfig.pwaInstallMoveEnabled ? resources && resources.InstallDesktopAppsBtnText : info.text;
            this.installOfficeAppsButtonText = this.capabilitiesService.capabilities.isPwaInUwp && this.installConfig.pwaInstallMoveEnabled ? resources && resources.InstallDesktopAppsBtnText : resources && resources.InstallOfficeAppsButtonText;
            this.installOfficeDescription = resources && resources.InstallOfficeDescription;
        }

        if (!this.installConfig.ecsInstallNoLicenseOptionsEnabled || info.scenario !== ButtonScenario.RedirectToMyAccountPage) {
            const installLink: InstallLink = {
                title: this.text,
                iconName: 'Download',
                url: this.navigateToUrl,
                description: this.installOfficeDescription,
                ariaLabel: this.ariaLabel,
                ariaDescribedBy: 'install-aria-desc-Download',
                cssClass: 'install-menu-item',
                getId: () => this.id
            };

            this.installLinks.push(installLink);
        }

        if (info.scenario === ButtonScenario.DirectInstall || (this.installConfig.ecsInstallNoLicenseOptionsEnabled && info.scenario === ButtonScenario.RedirectToMyAccountPage)) {
            const platform = this.deviceInfo.platform.platformType;
            this.showWizard = platform === PlatformType.PC || platform === PlatformType.Mac;
            if (this.authType === AuthType.OrgId) {
                // Temp solution for commercial user, an additional link to protal MyAccount page for other installs like Project/Visio
                // when a direct download found for the user; if not a direct link, the button will be taking the user to MyAccount page instead,
                // in that case, we don't need this link
                this.additionalLink = this.installConfig.commercialMySoftwarePageUrl;
                this.additionalLinkAriaLabel = resources && resources.InstallAdditionalLinkAriaLabel;
                this.additionalLinkText = resources && resources.InstallAdditionalLinkText;
                this.additionalLinkDescription = resources && resources.InstallAdditionalLinkDescription;

                switch (this.appFilter) {
                    case 'Office':
                        this.additionalLink += this.softwarePanelQueryParam + 'Panel_MicrosoftOffice_ClientDownload';
                        break;
                    case 'Project':
                        this.additionalLink += this.softwarePanelQueryParam + 'Panel_Project_ClientDownload';
                        break;
                    case 'Visio':
                        this.additionalLink += this.softwarePanelQueryParam + 'Panel_Visio_ClientDownload';
                        break;
                }

                const additionInstallLink: InstallLink = {
                    title: this.additionalLinkText,
                    iconName: 'ArrowRight',
                    url: this.additionalLink,
                    description: this.additionalLinkDescription,
                    ariaLabel: this.additionalLinkText,
                    ariaDescribedBy: 'install-aria-desc-additionalInstall',
                    cssClass: 'install-menu-item',
                    getId: () => this.additionalLinkId
                };

                this.installLinks.push(additionInstallLink);

                this.text = resources && resources.InstallOfficeAppsButtonText;
            }
        }

        this.menuModel = {
            id: 'install',
            items: this.installLinks,
            linkAriaLabel: this.installOfficeAppsButtonText,
            linkCssClass: 'install-button ' + (this.fluentRefresh ? 'generic-button--fluentvnext generic-button--fluentvnext--default' : 'generic-button generic-button--fluent install-button--fluent generic-button--fluent--secondary ms-bgc-w ms-bgc-tp-h ms-bgc-tp-a ms-fcl-tp ms-fcl-w-h ms-fcl-w-a'),
            linkTooltip: this.installOfficeAppsButtonText,
            rightAligned: this.rightAligned,
            cssClass: 'install-dropdown-container--fluent',
            hostComponentArea: this.componentArea
        };

        // Record the activation result, the scenario
        this.logScenario(info.scenario, info.url);
        this.clickData = this.getClickData(info.scenario);
    }

    /**
     * Gets instrumentation data for button click
     */
    private getClickData(scenario?: ButtonScenario): InstrumentationData {
        const params: InstrumentationParams = {};
        params[InstrumentationParam.Area] = this.componentArea;
        if (typeof scenario !== 'undefined') {
            params[InstrumentationParam.ElementId] = scenario.toString();
            params[InstrumentationParam.InstallableApp] = this.appFilter;

            // Log if OLS token is passed in for DirectInstall
            if (scenario === ButtonScenario.DirectInstall && this.navigateToUrl.indexOf('token=') > 0) {
                params[InstrumentationParam.OLSToken] = 'true';
            }
        } else {
            params[InstrumentationParam.ElementId] = this.additionalLinkId;
        }

        return {
            eventName: KnownInstrumentationEvents.InstallClick,
            params,
            metrics: []
        };
    }

    /**
     * Log click data
     */
    private logClickData(clickData: InstrumentationData): void {
        const clickTargetId = clickData.params[InstrumentationParam.ElementId] as string;
        const clickArea = clickData.params[InstrumentationParam.Area] as string;

        this.instrumentationService.logClickEvent(clickData.eventName, clickTargetId, clickArea, null, null, clickData.params, clickData.metrics);
        logUserAction({
            type: 'Click',
            id: clickTargetId,
            area: 'Hero',
            subArea: 'Install',
            result: 'Open',
            target: 'InstallDropdown',
            eventName: clickData.eventName
        });
    }

    /**
     * Logs the button scenario
     */
    private logScenario(scenario: ButtonScenario, url?: string): void {
        const instumentationParams: InstrumentationParams = {};
        instumentationParams[InstrumentationParam.ElementId] = scenario.toString();
        instumentationParams[InstrumentationParam.InstallableApp] = this.appFilter;

        if (scenario === ButtonScenario.DirectInstall && typeof url !== 'undefined' && url) {
            if (url.indexOf('token=') > 0) {
                instumentationParams[InstrumentationParam.InstallerType] = 'OLSInstaller';
            } else {
                instumentationParams[InstrumentationParam.InstallerType] = 'Generic Installer';
            }
        }

        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.InstallScenario, instumentationParams);
        logFeature({
            featureName: KnownFeatureNames.Install,
            eventName: KnownInstrumentationEvents.InstallScenario,
            area: 'Hero',
            params: {
                ElementId: scenario.toString(),
                InstallableApp: this.appFilter
            }
        });
    }
}
